import type { ApplicationDetailsDraft } from "@/lib/application-details"
import type { AvailableTransition, BenefitGrantDto, BenefitGrantReview, PsePaymentSplitDto, ServiceProviderPaymentDto } from "@/lib/benefit-types"
import type { VendorPaymentContext } from "@/lib/vendor-payment-context"
import type { MemberRemoval } from "@/lib/member-removal"
import type { GrantRemoval } from "@/lib/grant-removal"
import type { GrantAuthoringContext } from "@/lib/grant-authoring-context"
import type { EnergyIntakeDraft, EnergyServiceOption } from "@/lib/energy-intake"
import type { EnergyBillContext } from "@/lib/energy-editor-context"
import type { GrantEditContext, GrantTaskBasis, GrantTaskName } from "@/lib/grant-workspace"
import type { HouseholdInformationDraft, HouseholdMemberDraft } from "@/lib/household"
import type { IntakeDetailsDraft } from "@/lib/intake-details"
import type { PersonIdentityDraft, PotentialPersonMatch, newHouseholdMemberPerson } from "@/lib/person-identity"
import type { IncomeDraftFields } from "./benefitIncomeDrafts"
import type { ApplicationEligibilityProgramOption } from "@/lib/eligibility-selection"
import type { BenefitApplicationTargetYearDto, PersonDto } from "@/lib/benefit-types"

export type StagedHouseholdPerson = { personId?: string; newPerson?: ReturnType<typeof newHouseholdMemberPerson>; name: string }
export type GrantInformationTab = "award" | "evidence" | "history"
export interface GrantTask {
  benefitId: string; grant: BenefitGrantDto; review: BenefitGrantReview | null; task: GrantTaskName; editContext: GrantEditContext | null
  basis: GrantTaskBasis | null
}
interface PersonPickerFields {
  "picker.mode": "existing" | "new"; "picker.searchText": string; "picker.submittedSearch": string
  "picker.selected": PotentialPersonMatch | null; "picker.identity": PersonIdentityDraft; "picker.demographics": HouseholdMemberDraft
}
interface GrantFields {
  file: GrantAuthoringContext | null
  selectedInstance: string; editingGrant: BenefitGrantDto | null; grantAmount: string
  outcomeBefore: string; outcomeAfter: string; applicationDate: string
}
interface GrantTaskFields {
  task: GrantTask | null; attempt: { signature: string; key: string } | null
  "task.informationTab": GrantInformationTab; "task.explanation": string; "task.reasonCode": string
  "task.effectiveDate": string; "task.replacementAmount": string; "task.replacementRevision": string; "task.replacementDate": string
}
interface VendorPaymentFields {
  paymentDialog: (VendorPaymentContext & { payment?: ServiceProviderPaymentDto }) | null
  vendorId: string; paymentAmount: string; accountNumber: string
  secondVendorId: string; secondAccountNumber: string; secondPaymentAmount: string
}
interface PaymentSplitFields {
  splitContext: { paymentContext: VendorPaymentContext; split: PsePaymentSplitDto } | null; pseGasAccountNumber: string; pseElectricAccountNumber: string
}

/** Form/baseline contracts, not saved-state caches or a generic business patch protocol. */
export interface BenefitEditorFields extends IncomeDraftFields {
  intake: { draft: IntakeDetailsDraft; secondaryOptions: { id: string; label: string }[] }
  addresses: { draft: ApplicationDetailsDraft }
  housing: { draft: HouseholdInformationDraft }
  member: { draft: HouseholdMemberDraft; context: { person: PersonDto; applicantId: string } | null }
  "remove-member": { confirmation: MemberRemoval | null }
  "add-member": PersonPickerFields
  "non-cash": { mask: number; receivedFoodStamps: boolean; otherDescription: string }
  questionnaire: { pseEnabled: boolean; liheapEnabled: boolean; sheapEnabled: boolean; weatherization: boolean
    pastDueForgiveness: boolean; lifeThreateningCrisis: boolean | null; furnaceOlder: boolean; furnaceHeatsHome: boolean
    rentalPastDue: string; waterPastDue: string; coolingCost: string }
  "energy-bill": { identityContext: EnergyBillContext; appearance: string; lastName: string; firstName: string; middleName: string; ssn: string; dateOfBirth: string
    is18OrEmancipated: boolean; isStillLivingAtResidence: boolean; isSpouse: boolean; isDeceasedSpouse: boolean }
  "energy-intake": { draft: EnergyIntakeDraft; serviceOptions: EnergyServiceOption[] }
  "requested-benefits": { selected: number[]; options: ApplicationEligibilityProgramOption[] }
  "target-year": { selected: string; choices: BenefitApplicationTargetYearDto | null }
  "applicant-correction": PersonPickerFields & { applicant: string; removed: string | null; replacement: StagedHouseholdPerson | null
    pickerOpen: boolean; relationships: Record<string, string>; confirmed: boolean; reviewed: boolean
    roster: { id: string; name: string }[]; originalApplicantId: string }
  ownership: { search: string; selectedUserId: string | null }
  grant: GrantFields
  "grant-removal": { confirmation: GrantRemoval | null }
  "grant-task": GrantTaskFields
  "grant-review": { reason: string; explanation: string; sourceProgram: string; sourceYear: string; effectiveDate: string
    replacementAmount: string; replacementRevision: string; replacementDate: string }
  "vendor-payment": VendorPaymentFields
  "payment-split": PaymentSplitFields
  workflow: { action: "transition" | "adoption" | "denial-proof" | "revert"; target: string; reasonKey: string; explanation: string }
  lifecycle: { action: (AvailableTransition & { token: string | null }) | null }
}
export type BenefitEditorKind = keyof BenefitEditorFields
export type MemberEditorKind = "member" | "member-income"
export type FinancialEditorKind = "grant" | "grant-task" | "vendor-payment" | "payment-split"
export type KeyFor<K extends BenefitEditorKind> = K extends MemberEditorKind ? `${K}:${string}` : K
export type EditorKey = KeyFor<BenefitEditorKind>
export type KindFor<K extends EditorKey> = K extends `member:${string}` ? "member" : K extends `member-income:${string}` ? "member-income"
  : K & BenefitEditorKind
export type GroupFor<K extends BenefitEditorKind> = K extends FinancialEditorKind ? "grants" : KeyFor<K>
export type DraftGroup = Exclude<EditorKey, FinancialEditorKind> | "grants"
export type FieldsFor<G extends DraftGroup> = G extends "grants" ? GrantFields & GrantTaskFields & VendorPaymentFields & PaymentSplitFields
  : BenefitEditorFields[KindFor<Exclude<G, "grants">>]
export type DraftField<G extends DraftGroup> = keyof FieldsFor<G> & string

/** Some nested controls register after opening. Absence is explicit, never an invented default. */
type CapturedFields<K extends BenefitEditorKind> = Partial<BenefitEditorFields[K]>
export type EditorDraftData = { [K in BenefitEditorKind]: {
  kind: K; key: KeyFor<K>; group: GroupFor<K>; fields: CapturedFields<K>; baseline: CapturedFields<K>
} }[BenefitEditorKind]
export type EditorFieldChange = { [K in BenefitEditorKind]: { [F in keyof BenefitEditorFields[K] & string]: {
  sessionId: string; kind: K; key: KeyFor<K>; field: F; value: BenefitEditorFields[K][F]
} }[keyof BenefitEditorFields[K] & string] }[BenefitEditorKind]

// Exhaustive key maps: adding/removing a field requires updating its runtime boundary too.
const pickerFields = { "picker.mode": true, "picker.searchText": true, "picker.submittedSearch": true,
  "picker.selected": true, "picker.identity": true, "picker.demographics": true } as const
const fieldNames = {
  intake: { draft: true, secondaryOptions: true }, addresses: { draft: true }, housing: { draft: true }, member: { draft: true, context: true }, "remove-member": { confirmation: true },
  "add-member": pickerFields,
  "member-income": { lines: true, fieldErrors: true, anchorDate: true, defaultIncomeType: true },
  "household-income": { incomeType: true, error: true, members: true, anchorDate: true }, "no-income": { members: true },
  "non-cash": { mask: true, receivedFoodStamps: true, otherDescription: true },
  questionnaire: { pseEnabled: true, liheapEnabled: true, sheapEnabled: true, weatherization: true, pastDueForgiveness: true,
    lifeThreateningCrisis: true, furnaceOlder: true, furnaceHeatsHome: true, rentalPastDue: true, waterPastDue: true, coolingCost: true },
  "energy-bill": { identityContext: true, appearance: true, lastName: true, firstName: true, middleName: true, ssn: true, dateOfBirth: true,
    is18OrEmancipated: true, isStillLivingAtResidence: true, isSpouse: true, isDeceasedSpouse: true },
  "energy-intake": { draft: true, serviceOptions: true }, "requested-benefits": { selected: true, options: true }, "target-year": { selected: true, choices: true },
  "applicant-correction": { ...pickerFields, applicant: true, removed: true, replacement: true, pickerOpen: true,
    relationships: true, confirmed: true, reviewed: true, roster: true, originalApplicantId: true }, ownership: { search: true, selectedUserId: true },
  grant: { file: true, selectedInstance: true, editingGrant: true, grantAmount: true, outcomeBefore: true, outcomeAfter: true, applicationDate: true },
  "grant-removal": { confirmation: true },
  "grant-task": { task: true, attempt: true, "task.informationTab": true, "task.explanation": true, "task.reasonCode": true,
    "task.effectiveDate": true, "task.replacementAmount": true, "task.replacementRevision": true, "task.replacementDate": true },
  "grant-review": { reason: true, explanation: true, sourceProgram: true, sourceYear: true, effectiveDate: true,
    replacementAmount: true, replacementRevision: true, replacementDate: true },
  "vendor-payment": { paymentDialog: true, vendorId: true, paymentAmount: true, accountNumber: true,
    secondVendorId: true, secondAccountNumber: true, secondPaymentAmount: true },
  "payment-split": { splitContext: true, pseGasAccountNumber: true, pseElectricAccountNumber: true },
  workflow: { action: true, target: true, reasonKey: true, explanation: true }, lifecycle: { action: true },
} as const satisfies { [K in BenefitEditorKind]: { [F in keyof BenefitEditorFields[K]]: true } }

export function editorKind(key: string): BenefitEditorKind | null {
  const separator = key.indexOf(":"), kind = separator < 0 ? key : key.slice(0, separator)
  if (!Object.hasOwn(fieldNames, kind)) return null
  const member = kind === "member" || kind === "member-income"
  return (member ? separator >= 0 && key.length > separator + 1 : separator < 0) ? kind as BenefitEditorKind : null
}
export function editorGroup(key: EditorKey): DraftGroup {
  return ["grant", "grant-task", "vendor-payment", "payment-split"].includes(key) ? "grants" : key as DraftGroup
}
export function hasEditorField(kind: BenefitEditorKind, field: string): boolean {
  return Object.hasOwn(fieldNames, kind) && Object.hasOwn(fieldNames[kind], field)
}
export function validEditorDraft(data: EditorDraftData): boolean {
  return editorKind(data.key) === data.kind && editorGroup(data.key) === data.group
    && Object.keys(data.fields).every(field => hasEditorField(data.kind, field))
    && Object.keys(data.baseline).every(field => hasEditorField(data.kind, field))
}

/** The only erased-value boundary: sources originate in typed hooks/register calls, not server payloads.
 * Recheck key/group/field before recovering their correlation; never accept arbitrary Redux field names.
 */
export function captureEditorDraft(key: EditorKey, values: ReadonlyMap<string, { value(): unknown }>): EditorDraftData | null {
  const kind = editorKind(key)
  if (!kind) return null
  const fields = Object.fromEntries([...values].filter(([field]) => hasEditorField(kind, field)).map(([field, source]) => [field, source.value()]))
  return { kind, key, group: editorGroup(key), fields: structuredClone(fields), baseline: structuredClone(fields) } as EditorDraftData
}
export function editorFieldValue<G extends DraftGroup, F extends DraftField<G>>(
  active: EditorDraftData, group: G, field: F,
): FieldsFor<G>[F] | undefined {
  return active.group === group && hasEditorField(active.kind, field) ? (active.fields as Record<string, unknown>)[field] as FieldsFor<G>[F] : undefined
}
export function editorFieldChange<G extends DraftGroup, F extends DraftField<G>>(
  active: EditorDraftData & { sessionId: string }, group: G, field: F, value: NoInfer<FieldsFor<G>[F]>,
): EditorFieldChange | null {
  return active.group === group && hasEditorField(active.kind, field)
    ? { sessionId: active.sessionId, kind: active.kind, key: active.key, field, value } as EditorFieldChange : null
}
/** Called only after the reducer checked session, discriminator, key, field and legal phase. */
export function applyEditorField(active: EditorDraftData, change: EditorFieldChange, baseline: boolean) {
  (active.fields as Record<string, unknown>)[change.field] = change.value
  if (baseline) (active.baseline as Record<string, unknown>)[change.field] = change.value
}
